import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import IconosGenerales from "../../elementos/iconos/IconosGenerales";
import FilterListIcon from '@mui/icons-material/FilterList';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { visuallyHidden } from '@mui/utils';
import { styled } from '@mui/material/styles';
import PaginacionTabla from '../eventos/filtros/SelectPaginaTabla';
import SelectNumeroCursos from '../eventos/filtros/SelectNumeroElementos';
import SelectDinamico from '../eventos/filtros/SelectDinamico';
import SelectDinamicoLista from '../eventos/filtros/SelectDinamicoLista';
import Buscador from '../eventos/filtros/BuscadorDinamico';
import funcionOrdenarColumnas from '../eventos/filtros/OrdenarColumnas';
import { useNavigate, useLocation } from 'react-router-dom';
import CustomTooltip from './../eventos/avisos/Tooltip';
import BotonesAccion from "../../elementos/eventos/navegacion_y_accion/BotonesAccion";
import FormControlLabel from '@mui/material/FormControlLabel';
import { RiOrganizationChart } from "react-icons/ri";
import { FaBuilding } from "react-icons/fa6";


const StyledTableCell = styled(TableCell)(({ theme }) => ({
    textAlign: 'center  !important',
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: 'rgba(0, 60, 102, 0.8)',
        color: theme.palette.common.white,
        padding: '0px',
    },
    /*[`&.${tableCellClasses.body}`]: {
        fontSize: '14px ',
        textTransform: 'capitalize',
        padding: '0px',
    },*/
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    textAlign: 'center  !important',
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const WhiteTableSortLabel = styled(TableSortLabel)(({ theme }) => ({
    textAlign: 'center  !important',
    color: `${theme.palette.common.white} !important`,
    '&.Mui-active .MuiTableSortLabel-icon': {
        color: `${theme.palette.common.white} !important`,
    },
}));


function EnhancedTableHead(props) {

    const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <StyledTableRow>
                <StyledTableCell padding="checkbox">
                    <Checkbox
                        style={{ color: 'white' }}
                        indeterminate={numSelected > 0 && numSelected < rowCount}
                        checked={rowCount > 0 && numSelected === rowCount}
                        onChange={onSelectAllClick}
                        inputProps={{
                            'aria-label': 'select all desserts',
                        }}
                    />
                </StyledTableCell>
                {props.header.map((headCell) => (
                    <StyledTableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <WhiteTableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </WhiteTableSortLabel>
                    </StyledTableCell>
                ))}
            </StyledTableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};


function EnhancedTableToolbar(props) {
    const { numSelected, negocios, setEliminarSeleccion, mostrarDependientes, setMostrarDependientes, hayBusqueda } = props;
    const [estadoBusqueda, setEstadoBusqueda] = useState(false);
    const [escribiendo, setEscribiendo] = useState(false);
    const location = useLocation();
    
    const params = new URLSearchParams(location.search);
    const busquedaParam = params.get('busqueda') || '';
    const negocio = params.get("negocio");
    const tipo = params.get("tipo");

    const toggleSeleccion = (valor, atributo) => {
        if (atributo == "ocultar") {
            setEliminarSeleccion((prevState) => ({
                ...prevState,
                valor,
            }));
        }
    };

    const handleMostrarDependientes = (event) => {
        setMostrarDependientes(event.target.checked);
    };

    const busquedaActiva = hayBusqueda;

    useEffect(() => {
        if (!hayBusqueda) {
            setMostrarDependientes(false);
        }
    }, [hayBusqueda, setMostrarDependientes]);


    const tipos = [
        { Id: "interno", nombre: "Interno" },
        { Id: "organigrama", nombre: "Organigrama" }
    ];

    return (
        <>
            <Toolbar
                sx={{
                    pl: { sm: 2 },
                    pr: { xs: 1, sm: 1 },
                    ...(numSelected > 0 && {
                        bgcolor: (theme) =>
                            alpha(theme.palette.common.white, theme.palette.action.activatedOpacity),
                    }),
                }}
            >
                {numSelected > 0 ? (
                    <Typography
                        sx={{ flex: '1 1 100%' }}
                        color="inherit"
                        variant="subtitle1"
                        component="div"
                    >
                        {numSelected} selecionado(s)
                    </Typography>
                ) : (
                    <Typography
                        sx={{ flex: '1 1 40%' }}
                        variant="h6"
                        id="tableTitle"
                        component="div"
                    >
                        <BotonesAccion
                            texto="Crear Departamento"
                            to="/gestionpersonas/departamentos/crearDepartamento"
                        />
                    </Typography>
                )}


                {numSelected > 0 ? (
                    <>
                        {[
                            { mensaje: "Eliminar select", id: "eliminarSelect", handler: () => toggleSeleccion(false, "ocultar") },
                        ].map(({ mensaje, id, handler }) => (
                            <CustomTooltip key={id} mensaje={mensaje}>
                                <div
                                    onClick={handler}
                                    style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    width: '40px', 
                                    height: '40px',
                                    }}
                                >
                                    <IconosGenerales papeleraT="true" id={id} style={{width: '40px', height: '40px'}}/>
                                </div>
                            </CustomTooltip>
                        ))}
                    </>

                ) : (
                    <>

                        <SelectDinamicoLista opciones={negocios.sort((a, b) => a.nombre.localeCompare(b.nombre))} parametroURL="negocio" etiqueta="Negocio" />
                        <SelectDinamicoLista opciones={tipos} parametroURL="tipo" etiqueta="Tipo" />
                        <Buscador
                            placeholder="Buscar Departamento"
                            tooltip="Ej: Nombre"
                            palabra={busquedaParam}
                            estadoBusqueda={estadoBusqueda}
                            setEstadoBusqueda={setEstadoBusqueda}
                            escribiendo={escribiendo}
                            setEscribiendo={setEscribiendo}
                        />

                        <FormControlLabel 
                            style={{ marginLeft: '5px' }} 
                            control={
                                <Checkbox 
                                    checked={mostrarDependientes}
                                    onChange={handleMostrarDependientes} 
                                    disabled={!busquedaActiva || !negocio}
                                />
                            } 
                            label="Mostrar dependientes" 
                        />

                        <BotonesAccion
                            icono="delete"
                            to="/gestionpersonas/departamentos"
                        />

                    </>
                )}
            </Toolbar >
        </>
    );
}

EnhancedTableToolbar.propTypes = {
    numSelected: PropTypes.number.isRequired,
};


function EnhancedTable(props) {
    const navigate = useNavigate();
    const location = useLocation();
    const {
        data,
        header,
        meta,
        negocios,
        departamentos,
        todosLosDepartamentos,
        selected,
        setSelected,
        eliminarSeleccion,
        setEliminarSeleccion
    } = props;

    const params = new URLSearchParams(location.search);
    const busqueda = params.get("busqueda");
    const negocio = params.get("negocio");
    const tipo = params.get("tipo");
    const hayBusqueda = Boolean(busqueda);
      const orderParam = params.get("orden") || 'ASC';
    const orderByParam = params.get("ordenarColumna") || 'nombre';

    const [order, setOrder] = React.useState(orderParam.toLowerCase());
    const [orderBy, setOrderBy] = React.useState(orderByParam);
    const [dependencias, setDependencias] = React.useState(new Map());
    const [mostrarDependientes, setMostrarDependientes] = useState(false);    useEffect(() => {
        const newOrder = params.get("orden") || 'ASC';
        const newOrderBy = params.get("ordenarColumna") || 'nombre';
        
        setOrder(newOrder.toLowerCase());
        setOrderBy(newOrderBy);
    }, [location.search]);

    useEffect(() => {
        if (negocio != null && data.length > 0) {
            let dependenciaTemp = new Map();
            data.forEach((departamento) => {
                departamento.Negocios.forEach((deptNegocio) => {
                    if(deptNegocio.Id == negocio && deptNegocio.DepartamentoEmpresa.dependencia_id != null)
                        dependenciaTemp.set(departamento.Id, deptNegocio.DepartamentoEmpresa.dependencia_info.departamento);
                });
            });
            setDependencias(dependenciaTemp);
        } else {
            setDependencias(new Map());
        }
    }, [data, negocio]);
    
    const obtenerDepartamentosFiltrados = useMemo(() => {
        if (!data || !Array.isArray(data)) {
            return [];
        }

        if(mostrarDependientes && hayBusqueda && todosLosDepartamentos && Array.isArray(todosLosDepartamentos)){
            const idDepartamentosActuales = data.map(dept => dept?.Id).filter(id => id != null);
            
            let departamentosDependientes = [];
            
            todosLosDepartamentos.forEach((departamento) => {
                if(departamento && !idDepartamentosActuales.includes(departamento.Id)) {
                    if (departamento.Negocios && Array.isArray(departamento.Negocios)) {
                        for(let i = 0; i < departamento.Negocios.length; i++) {
                            const deptNegocio = departamento.Negocios[i];
                            if(deptNegocio && 
                               deptNegocio.Id == negocio && 
                               deptNegocio.DepartamentoEmpresa && 
                               deptNegocio.DepartamentoEmpresa.dependencia_id != null &&
                               deptNegocio.DepartamentoEmpresa.dependencia_info) {
                                if(idDepartamentosActuales.includes(deptNegocio.DepartamentoEmpresa.dependencia_info.id)) {
                                    departamentosDependientes.push(departamento);
                                    idDepartamentosActuales.push(departamento.Id);
                                    break;
                                }
                            }
                        }
                    }
                }
            });

            return [...data, ...departamentosDependientes];
        }

        return data;
    }, [data, mostrarDependientes, hayBusqueda, todosLosDepartamentos, negocio]);

    useEffect(() => {
        if (!data || !Array.isArray(data)) {
            return;
        }

        let dependenciaTemp = new Map();
        
        if(negocio != null){
            data.forEach((departamento) => {
                if (departamento && departamento.Negocios && Array.isArray(departamento.Negocios)) {
                    departamento.Negocios.forEach((deptNegocio) => { 
                        if(deptNegocio && deptNegocio.Id == negocio && 
                           deptNegocio.DepartamentoEmpresa && 
                           deptNegocio.DepartamentoEmpresa.dependencia_id != null &&
                           deptNegocio.DepartamentoEmpresa.dependencia_info) {
                            dependenciaTemp.set(departamento.Id, deptNegocio.DepartamentoEmpresa.dependencia_info.departamento);
                        }
                    });
                }
            });

            if(mostrarDependientes && hayBusqueda && todosLosDepartamentos && Array.isArray(todosLosDepartamentos)){
                let todosLosDepartamentosFiltrados = todosLosDepartamentos.filter((departamento) =>
                    departamento && departamento.Negocios && 
                    departamento.Negocios.some((deptNegocio) => deptNegocio && deptNegocio.Id == negocio)
                );
                
                todosLosDepartamentosFiltrados.forEach((departamento) => {
                    if (departamento && departamento.Negocios && Array.isArray(departamento.Negocios)) {
                        departamento.Negocios.forEach((deptNegocio) => {
                            if(deptNegocio && 
                               deptNegocio.Id == negocio && 
                               deptNegocio.DepartamentoEmpresa && 
                               deptNegocio.DepartamentoEmpresa.dependencia_id != null &&
                               deptNegocio.DepartamentoEmpresa.dependencia_info) {
                                dependenciaTemp.set(departamento.Id, deptNegocio.DepartamentoEmpresa.dependencia_info.departamento);
                            }
                        });
                    }
                });
            }
        }
        
        setDependencias(dependenciaTemp);
    }, [data, negocio, mostrarDependientes, hayBusqueda, todosLosDepartamentos]);

    const handleRequestSort = (event, property) => {
        const nuevaURL = funcionOrdenarColumnas(orderBy, order, setOrderBy, setOrder)(event, property);
        navigate(nuevaURL);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelected = data.map((n) => n.Id);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event, id) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }
        setSelected(newSelected);
    };    const isSelected = (id) => selected.indexOf(id) !== -1;

    const manejarPaginacion = useMemo(() => {
        // Si no estamos mostrando dependientes
        if (!mostrarDependientes || !hayBusqueda) {
            return {
                totalPages: meta?.totalPages || 1,
                totalRegistros: meta?.total || 0,
                paginaActual: meta?.current_page || 1
            };
        }
        
        // Si estamos mostrando dependientes, calcular localmente
        const datosFiltrados = obtenerDepartamentosFiltrados;
        const registrosPorPagina = meta?.per_page || 10;
        const totalRegistros = datosFiltrados.length;
        const totalPaginas = Math.ceil(totalRegistros / registrosPorPagina);
        
        return {
            totalPages: totalPaginas,
            totalRegistros: totalRegistros,
            paginaActual: parseInt(params.get("pagina") || "1")
        };
    }, [mostrarDependientes, hayBusqueda, obtenerDepartamentosFiltrados, meta, params]);

    const datosAMostrar = useMemo(() => {
        // Si no estamos mostrando dependientes, usar datos del backend directamente
        if (!mostrarDependientes || !hayBusqueda) {
            return data || [];
        }
        
        // Si estamos mostrando dependientes, filtrar y paginar localmente
        const datosFiltrados = obtenerDepartamentosFiltrados;
        const registrosPorPagina = meta?.per_page || 10;
        const paginaActual = parseInt(params.get("pagina") || "1");
        const inicio = (paginaActual - 1) * registrosPorPagina;
        const fin = inicio + registrosPorPagina;
        
        return datosFiltrados.slice(inicio, fin);
    }, [mostrarDependientes, hayBusqueda, obtenerDepartamentosFiltrados, params, meta]);

    return (
        <Box sx={{ width: '100%' }}>
            <div>
                <Paper sx={{ width: '98%', mb: 2 }}>

                    <EnhancedTableToolbar
                        numSelected={selected.length}
                        negocios={negocios}
                        departamentos={departamentos}
                        eliminarSeleccion={eliminarSeleccion}
                        setEliminarSeleccion={setEliminarSeleccion}
                        mostrarDependientes={mostrarDependientes}
                        setMostrarDependientes={setMostrarDependientes}
                        hayBusqueda={hayBusqueda}
                    />

                    <TableContainer>
                        <Table sx={{ maxWidth: 2000, minWidth: 1300, }} aria-labelledby="tableTitle">
                            <EnhancedTableHead
                                numSelected={selected.length}
                                order={order}
                                orderBy={orderBy}
                                onSelectAllClick={handleSelectAllClick}
                                onRequestSort={handleRequestSort}
                                rowCount={datosAMostrar.length}
                                header={header}
                            />
                            <TableBody>
                                {datosAMostrar.map((row, index) => {
                                    const isItemSelected = isSelected(row.Id);
                                    const labelId = `enhanced-table-checkbox-${index}`;
                                    return (
                                        <StyledTableRow
                                            hover
                                            role="checkbox"
                                            aria-checked={isItemSelected}
                                            tabIndex={-1}
                                            key={row.Id}
                                            selected={isItemSelected}
                                        >
                                            <StyledTableCell padding="checkbox">
                                                <Checkbox
                                                    color="default"
                                                    onClick={(event) => { handleClick(event, row.Id) }}
                                                    checked={isItemSelected}
                                                    inputProps={{ 'aria-labelledby': labelId }}
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell
                                                component="th"
                                                id={labelId}
                                                scope="row"
                                                padding="none"
                                            >
                                                <Box sx={{ display: 'flex', justifyContent: 'center', transform: 'scale(1.5)', marginRight: '25px' }}>
                                                    {row.tipo === 'organigrama' ? <RiOrganizationChart/> : <FaBuilding/>}
                                                </Box>
                                            </StyledTableCell>
                                            <StyledTableCell
                                                component="th"
                                                id={labelId}
                                                scope="row"
                                                padding="none"
                                            >
                                                {row.nombre}
                                            </StyledTableCell>
                                            <StyledTableCell align="left">
                                                {dependencias.get(row.Id) ? dependencias.get(row.Id) : negocio ? 'Sin dependencia' : 'Selecciona un negocio' }
                                            </StyledTableCell>
                                            <StyledTableCell align="left">{row.Negocios ? row.Negocios.length : 0}</StyledTableCell>
                                            <StyledTableCell align="right">
                                                <IconosGenerales
                                                    tabla="true"
                                                    id="edit"
                                                    to={`/gestionpersonas/departamentos/editarDepartamento/${row.Id}`}
                                                />
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                </Paper>
            </div>

            <PaginacionTabla
                totalPages={manejarPaginacion.totalPages}
            />
            <SelectNumeroCursos
                perPage={meta.per_page || 10}
            />

            <Typography
                variant="body2"
                sx={{
                    mr: '2%',
                    textAlign: 'right',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    gap: "8px", marginTop: "-25px",
                    color: 'rgba(0, 60, 102, 0.8)',
                }}
                >
                Total registros: {manejarPaginacion.totalRegistros}
            </Typography>

        </Box>
    );
}

EnhancedTable.propTypes = {
    data: PropTypes.array.isRequired,
    header: PropTypes.array.isRequired,
    meta: PropTypes.object.isRequired,
};

export default EnhancedTable;